/**
 * seededShuffle.js — deterministic, seed-stable Fisher–Yates shuffle.
 *
 * Used to randomise MCQ option order at render time so the correct
 * answer is not always in the same slot. The order is STABLE within a
 * single attempt (same seed → same order) but differs across attempts
 * (attempt number is part of the seed). Grading must always compare by
 * value/ID, never by array index, for this to be safe.
 */

/** mulberry32 PRNG seeded from a string. Returns a () => [0,1) fn. */
function makeRng(seedStr) {
  let seed = 0;
  const s = String(seedStr || "");
  for (let i = 0; i < s.length; i++) seed = (Math.imul(seed, 31) + s.charCodeAt(i)) >>> 0;
  return function () {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Return a new array with `arr` shuffled deterministically by `seedStr`. */
export function seededShuffle(arr, seedStr) {
  const a = Array.isArray(arr) ? [...arr] : [];
  const rand = makeRng(seedStr);
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    const tmp = a[i];
    a[i] = a[j];
    a[j] = tmp;
  }
  return a;
}

/**
 * Return a shallow copy of a question with its `options` reordered by a
 * seeded shuffle. `correct_answer` is preserved by value, so grading
 * stays correct. No-ops when there are fewer than 2 options.
 */
export function shuffleQuestionOptions(q, seedStr) {
  if (!q || !Array.isArray(q.options) || q.options.length < 2) return q;
  return { ...q, options: seededShuffle(q.options, seedStr) };
}
